import { AxiosError } from "axios";
import { modalNotificationsService } from "../notifications/ModalNotificationService";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { translationService } from "../translation/TranslationService";

import { BaseApiError } from "./BaseApiError";

interface BadRequestErrors {
  formValidationErrors: FormValidationErrors | undefined;
  pageValidationErrors: string[] | undefined;
}

export interface ErrorMessageWithContext {
  Context: { [key: string]: string } | string | number;
  MessageKey: string;
}

export type FormValidationErrors = { [key: string]: string } | undefined;

export interface BadRequestErrorKeys {
  errors: { [key: string]: string[] };
  errorKey: string;
}

export interface BadRequestError extends AxiosError<BadRequestErrorKeys> {
  showErrorInSnackbar(): void;
}

export class BadRequestError extends BaseApiError implements BadRequestError {
  errors: BadRequestErrorKeys["errors"];
  errorKey: BadRequestErrorKeys["errorKey"];

  constructor(error: AxiosError<BadRequestErrorKeys>) {
    super();
    Object.assign(this, error);
    this.errors = this.response?.data?.errors || {};
    this.errorKey = !this.response?.data?.errors ? String(this.response?.data) || "" : "";

    Object.keys(this.errors).forEach((fieldName: string) => {
      if (fieldName) this.makeErrorKeyFirstLetterLowercase(fieldName);
    });
  }

  public getFormErrors = (): FormValidationErrors => {
    return this.extractErrors().formValidationErrors;
  };

  public getPageErrors = (): string[] | undefined => {
    return this.extractErrors().pageValidationErrors;
  };

  public showErrorInForm = () => {
    if (!this.formActions) return;

    const formErrors = this.getFormErrors();

    if (formErrors) this.formActions.setErrors(formErrors);
  };

  public showErrorInSnackbar = () => {
    const pageErrors: string[] | undefined = this.getPageErrors();
    if (!pageErrors || !pageErrors.length) return;
    let validationTranslation: string = "";

    pageErrors.forEach((error) => {
      var translatedFieldError = this.getTranslatedError(error);

      validationTranslation = validationTranslation.concat(translatedFieldError);
    });
    snackbarNotificationsService.showError(validationTranslation);
  };

  showErrorInModal() {
    const pageErrors: string[] | undefined = this.getPageErrors();
    if (!pageErrors || !pageErrors.length) return;
    let validationTranslation: string = "";

    pageErrors.forEach((error) => {
      var translatedFieldError = this.getTranslatedError(error);

      validationTranslation = validationTranslation.concat(translatedFieldError);
    });

    const translatedModalTitle = translationService.translate(this.modalTitleKey || "");

    modalNotificationsService.showError({
      titleKey: translatedModalTitle,
      messageKey: validationTranslation,
    });
  }

  private extractErrors = (): BadRequestErrors => {
    if (!this.hasValidationErrors())
      return { formValidationErrors: undefined, pageValidationErrors: undefined };

    const formFieldKeys = this.extractFormFieldKeys();

    let formValidationErrors: any = {};
    let pageValidationErrors: string[] = [];

    Object.keys(this.errors).forEach((fieldName: string) => {
      const errorMessage = this.getErrorMessageForField(fieldName);
      if (!errorMessage) return;

      if (formFieldKeys.includes(fieldName)) formValidationErrors[fieldName] = errorMessage;
      else pageValidationErrors.push(errorMessage);
    });

    if (this.errorKey) {
      var errorMessageWithContext: ErrorMessageWithContext = {
        Context: "",
        MessageKey: this.errorKey,
      };
      pageValidationErrors.push(JSON.stringify(errorMessageWithContext));
    }

    return { formValidationErrors, pageValidationErrors };
  };

  private hasValidationErrors = (): boolean => {
    return !!Object.keys(this.errors).length || !!this.errorKey;
  };

  private getErrorMessageForField(fieldName: string) {
    return this.errors[fieldName] && this.errors[fieldName][0];
  }

  private extractFormFieldKeys = () => {
    if (!this.formFieldsValues) return [];
    return Object.keys(this.formFieldsValues);
  };

  private makeErrorKeyFirstLetterLowercase = (fieldName: string) => {
    const fieldNameCopy = fieldName;
    if (this.errorKeyStartsWithUpperCase(fieldNameCopy)) {
      const lowercaseFirstLetterFieldName =
        fieldNameCopy.charAt(0).toLowerCase() + fieldNameCopy.slice(1);
      this.errors[lowercaseFirstLetterFieldName] = this.errors[fieldName];
      delete this.errors[fieldName];
    }
  };

  private errorKeyStartsWithUpperCase = (fieldNameCopy: string) => {
    return fieldNameCopy.charAt(0) === fieldNameCopy.charAt(0).toUpperCase();
  };

  private getTranslatedError(error: string) {
    let fieldError = error.replaceAll('^"|"$', "");
    var translatedFieldError = "";
    var errorMessageAndContext: ErrorMessageWithContext = this.mapErrorWithMessageWithContext(
      JSON.parse(fieldError)
    );
    translatedFieldError = this.translateComplexValidation(
      errorMessageAndContext,
      translatedFieldError
    );
    return translatedFieldError;
  }

  private translateComplexValidation = (
    errorMessageAndContext: ErrorMessageWithContext,
    validationTranslation: string
  ) => {
    let translationResult = "";
    if (this.errorHasContext(errorMessageAndContext))
      translationResult = this.translateErrorWithContext(errorMessageAndContext);
    else translationResult = translationService.translate(errorMessageAndContext.MessageKey);

    validationTranslation = validationTranslation.concat(translationResult).concat(" ");
    return validationTranslation;
  };

  private mapErrorWithMessageWithContext = (source: ErrorMessageWithContext) => {
    const destination: ErrorMessageWithContext = {
      Context: source.Context,
      MessageKey: source.MessageKey,
    };
    return destination;
  };

  private translateErrorWithContext(errorMessageAndContext: ErrorMessageWithContext): string {
    if (
      typeof errorMessageAndContext.Context === "string" ||
      typeof errorMessageAndContext.Context === "number"
    ) {
      const context = { context: errorMessageAndContext.Context };
      return translationService.translate(errorMessageAndContext.MessageKey, context);
    } else if (Object.keys(errorMessageAndContext.Context).length > 0)
      return translationService.translate(
        errorMessageAndContext.MessageKey,
        errorMessageAndContext.Context
      );

    return translationService.translate(errorMessageAndContext.MessageKey);
  }

  private errorHasContext(errorMessageAndContext: ErrorMessageWithContext) {
    return errorMessageAndContext.Context;
  }
}
